import { useState, useMemo, memo } from 'react';
import { Checkbox, Button, Tooltip, Tag, Badge } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
  LinkOutlined,
  DownOutlined,
  RightOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { PropertyData } from '@/services/scraper.service';
import { StreetGroup, PropertyGroup, calculateGroupStats } from './propertyGrouping';
import { SOURCE_LOGO_MAP } from './propertyGrouping.constants';
import { formatCurrency } from './propertyGrouping.utils';
import './StreetGroupCard.css';

interface StreetGroupCardProps {
  group: StreetGroup;
  selectedProperties: Set<string>;
  onPropertyToggle: (property: PropertyData) => void;
  onSelectAllInStreet: (group: StreetGroup, select: boolean) => void;
  onSelectAllInCondo: (condo: PropertyGroup, select: boolean) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
}

const PropertyItem = memo(({
  property,
  isSelected,
  onToggle,
  disabled,
}: {
  property: PropertyData;
  isSelected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) => {
  const logo = SOURCE_LOGO_MAP[property.fonte?.toLowerCase()];

  return (
    <div className={`property-item ${isSelected ? 'selected' : ''}`}>
      <Checkbox
        checked={isSelected}
        onChange={onToggle}
        disabled={disabled}
        className="property-checkbox"
      />
      <div className="property-content">
        <div className="property-header">
          {logo ? (
            <img src={logo} alt={property.fonte} className="fonte-logo" />
          ) : (
            <Tag className="fonte-tag">{property.fonte}</Tag>
          )}
          {property.tipo_imovel && (
            <Tag color={
              property.tipo_imovel.toLowerCase().includes('apartamento') ? 'blue' :
              property.tipo_imovel.toLowerCase().includes('casa') ? 'orange' :
              property.tipo_imovel.toLowerCase().includes('terreno') ? 'brown' :
              property.tipo_imovel.toLowerCase().includes('comercial') ? 'cyan' : 'default'
            } style={{ fontSize: 10 }}>
              {property.tipo_imovel}
            </Tag>
          )}
          {isSelected && (
            <Tag color="green" icon={<CheckCircleOutlined />} className="selected-tag">
              Selecionado
            </Tag>
          )}
        </div>
        <Tooltip title={property.titulo}>
          <div className="property-title">{property.titulo}</div>
        </Tooltip>
        <div className="property-location">
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {property.endereco_completo}
        </div>
        <div className="property-details">
          {property.quartos_num && <span>🛏️ {property.quartos_num} qtos</span>}
          {property.banheiros_num && <span>🚿 {property.banheiros_num} ban</span>}
          {property.vagas_num && <span>🚗 {property.vagas_num} vag</span>}
          {property.metragem_num && <span>📏 {property.metragem_num}m²</span>}
          {property.distancia_metros && (
            <span>📍 {property.distancia_metros}m</span>
          )}
        </div>
      </div>
      <div className="property-price">
        <div className="price-value">{formatCurrency(property.valor_venda_num)}</div>
        {property.metragem_num && property.valor_venda_num && (
          <div className="price-per-m2">
            {formatCurrency(property.valor_venda_num / property.metragem_num)}/m²
          </div>
        )}
      </div>
      <div className="property-actions">
        <Button
          type="link"
          icon={<LinkOutlined />}
          href={property.url}
          target="_blank"
          size="small"
        >
          Ver
        </Button>
      </div>
    </div>
  );
});

// Componente para exibir um condominio dentro da rua
const CondominiumSection = memo(({
  condo,
  selectedProperties,
  onPropertyToggle,
  onSelectAll,
  disabled,
}: {
  condo: PropertyGroup;
  selectedProperties: Set<string>;
  onPropertyToggle: (property: PropertyData) => void;
  onSelectAll: (condo: PropertyGroup, select: boolean) => void;
  disabled: boolean;
}) => {
  const [expanded, setExpanded] = useState(true);
  const stats = useMemo(() => calculateGroupStats(condo.properties), [condo.properties]);

  const selectedCount = condo.properties.filter(p => selectedProperties.has(p.url)).length;
  const allSelected = selectedCount === condo.totalCount;
  const someSelected = selectedCount > 0 && selectedCount < condo.totalCount;

  const handleSelectAll = (e: CheckboxChangeEvent | React.MouseEvent) => {
    e.stopPropagation();
    onSelectAll(condo, !allSelected);
  };

  return (
    <div className="condo-section">
      <div className="condo-header" onClick={() => setExpanded(!expanded)}>
        <div className="condo-expand-icon">
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </div>

        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          className="condo-checkbox"
        />

        <div className="condo-info">
          <div className="condo-title">
            <Tag color="purple" className="condo-tag">
              <HomeOutlined style={{ marginRight: 4 }} />
              Condominio
            </Tag>
            <span className="condo-name">{condo.condominiumName}</span>
          </div>
          <div className="condo-subtitle">
            <Badge
              count={condo.totalCount}
              style={{
                backgroundColor: selectedCount === condo.totalCount ? '#52c41a' :
                  selectedCount > 0 ? '#faad14' : '#d9d9d9',
                marginRight: 8,
              }}
            />
            <span>
              {condo.totalCount} {condo.totalCount === 1 ? 'imovel' : 'imoveis'}
              {selectedCount > 0 && ` (${selectedCount} selecionado${selectedCount > 1 ? 's' : ''})`}
            </span>
          </div>
        </div>

        <div className="condo-stats">
          {stats.avgPrice && (
            <div className="stat">
              <span className="stat-label">Preco medio</span>
              <span className="stat-value">{formatCurrency(stats.avgPrice)}</span>
            </div>
          )}
          {stats.avgPricePerM2 && (
            <div className="stat">
              <span className="stat-label">VU medio</span>
              <span className="stat-value">{formatCurrency(stats.avgPricePerM2)}/m²</span>
            </div>
          )}
        </div>

        {!disabled && (
          <Button
            type={allSelected ? 'default' : 'primary'}
            size="small"
            onClick={handleSelectAll}
            className="select-all-btn"
          >
            {allSelected ? 'Remover' : 'Selecionar'}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="condo-content">
          {condo.properties.map((property) => (
            <PropertyItem
              key={property.url}
              property={property}
              isSelected={selectedProperties.has(property.url)}
              onToggle={() => onPropertyToggle(property)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
});

function StreetGroupCardComponent({
  group,
  selectedProperties,
  onPropertyToggle,
  onSelectAllInStreet,
  onSelectAllInCondo,
  disabled = false,
  defaultExpanded = true,
}: StreetGroupCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showLooseProperties, setShowLooseProperties] = useState(true);

  // Calcular stats de todos os imoveis da rua
  const allProperties = useMemo(() => {
    const props: PropertyData[] = [];
    group.condominiums.forEach(c => props.push(...c.properties));
    props.push(...group.looseProperties);
    return props;
  }, [group]);

  const stats = useMemo(() => calculateGroupStats(allProperties), [allProperties]);

  const selectedCount = allProperties.filter(p => selectedProperties.has(p.url)).length;
  const allSelected = selectedCount === group.totalCount;
  const someSelected = selectedCount > 0 && selectedCount < group.totalCount;

  const looseSelectedCount = group.looseProperties.filter(p => selectedProperties.has(p.url)).length;
  const allLooseSelected = looseSelectedCount === group.looseProperties.length && group.looseProperties.length > 0;
  const someLooseSelected = looseSelectedCount > 0 && looseSelectedCount < group.looseProperties.length;

  const handleSelectAllStreet = (e: CheckboxChangeEvent | React.MouseEvent) => {
    e.stopPropagation();
    onSelectAllInStreet(group, !allSelected);
  };

  const handleSelectAllLoose = (e: React.MouseEvent) => {
    e.stopPropagation();
    group.looseProperties.forEach(property => {
      const isSelected = selectedProperties.has(property.url);
      if ((!allLooseSelected && !isSelected) || (allLooseSelected && isSelected)) {
        onPropertyToggle(property);
      }
    });
  };

  const hasCondos = group.condominiums.length > 0;
  const hasLoose = group.looseProperties.length > 0;

  return (
    <div className={`street-group-card ${expanded ? 'expanded' : ''}`}>
      <div className="street-header" onClick={() => setExpanded(!expanded)}>
        <div className="street-expand-icon">
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </div>

        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAllStreet}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          className="street-checkbox"
        />

        <div className="street-info">
          <div className="street-title">
            <EnvironmentOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            <span className="street-name">{group.displayName}</span>
          </div>
          <div className="street-subtitle">
            <Badge
              count={group.totalCount}
              style={{
                backgroundColor: selectedCount === group.totalCount ? '#52c41a' :
                  selectedCount > 0 ? '#faad14' : '#d9d9d9',
                marginRight: 8,
              }}
            />
            <span>
              {group.totalCount} {group.totalCount === 1 ? 'imovel' : 'imoveis'}
              {hasCondos && ` • ${group.condominiums.length} condominio${group.condominiums.length > 1 ? 's' : ''}`}
              {hasLoose && ` • ${group.looseProperties.length} avulso${group.looseProperties.length > 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        <div className="street-stats">
          {stats.avgPrice && (
            <div className="stat">
              <span className="stat-label">Preco medio</span>
              <span className="stat-value">{formatCurrency(stats.avgPrice)}</span>
            </div>
          )}
          {stats.avgArea && (
            <div className="stat">
              <span className="stat-label">Area media</span>
              <span className="stat-value">{Math.round(stats.avgArea)}m²</span>
            </div>
          )}
          {stats.avgPricePerM2 && (
            <div className="stat">
              <span className="stat-label">VU medio</span>
              <span className="stat-value">{formatCurrency(stats.avgPricePerM2)}/m²</span>
            </div>
          )}
        </div>

        {!disabled && (
          <Button
            type={allSelected ? 'default' : 'primary'}
            size="small"
            onClick={handleSelectAllStreet}
            className="select-all-btn"
          >
            {allSelected ? 'Remover todos' : 'Selecionar todos'}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="street-content">
          {/* Condominios */}
          {group.condominiums.map((condo) => (
            <CondominiumSection
              key={condo.id}
              condo={condo}
              selectedProperties={selectedProperties}
              onPropertyToggle={onPropertyToggle}
              onSelectAll={onSelectAllInCondo}
              disabled={disabled}
            />
          ))}

          {/* Imoveis avulsos */}
          {hasLoose && (
            <div className="loose-section">
              <div
                className="loose-header"
                onClick={() => setShowLooseProperties(!showLooseProperties)}
              >
                <div className="loose-expand-icon">
                  {showLooseProperties ? <DownOutlined /> : <RightOutlined />}
                </div>

                <Checkbox
                  checked={allLooseSelected}
                  indeterminate={someLooseSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAllLoose(e as unknown as React.MouseEvent);
                  }}
                  disabled={disabled}
                  className="loose-checkbox"
                />

                <div className="loose-info">
                  <span className="loose-title">
                    Imoveis avulsos
                  </span>
                  <Badge
                    count={group.looseProperties.length}
                    style={{
                      backgroundColor: looseSelectedCount === group.looseProperties.length ? '#52c41a' :
                        looseSelectedCount > 0 ? '#faad14' : '#d9d9d9',
                      marginLeft: 8,
                    }}
                  />
                  {looseSelectedCount > 0 && (
                    <span className="loose-selected">
                      ({looseSelectedCount} selecionado{looseSelectedCount > 1 ? 's' : ''})
                    </span>
                  )}
                </div>

                {!disabled && (
                  <Button
                    type={allLooseSelected ? 'default' : 'link'}
                    size="small"
                    onClick={handleSelectAllLoose}
                  >
                    {allLooseSelected ? 'Remover' : 'Selecionar'}
                  </Button>
                )}
              </div>

              {showLooseProperties && (
                <div className="loose-content">
                  {group.looseProperties.map((property) => (
                    <PropertyItem
                      key={property.url}
                      property={property}
                      isSelected={selectedProperties.has(property.url)}
                      onToggle={() => onPropertyToggle(property)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const StreetGroupCard = memo(StreetGroupCardComponent);
